import { Injectable } from '@angular/core';
import {
  CategoryStorageKey,
  DateStorageKey,
  DOCUMENTS_CATEGORIES_KEY,
  DOCUMENTS_DATE_FROM_KEY,
  DOCUMENTS_DATE_TO_KEY,
  DOCUMENTS_FILE_TYPE_KEY,
  DOCUMENTS_SEARCH_KEY,
  DOCUMENTS_SORT_KEY,
  EVENTS_CATEGORIES_KEY,
  EVENTS_DATE_FROM_KEY,
  EVENTS_DATE_TO_KEY,
  EVENTS_SEARCH_KEY,
  EVENTS_SORT_KEY,
  GLOBAL_CATEGORIES_KEY,
  GLOBAL_DATE_FROM_KEY,
  GLOBAL_DATE_TO_KEY,
  GLOBAL_SEARCH_KEY,
  ICategory,
  NEWS_CATEGORIES_KEY,
  NEWS_DATE_FROM_KEY,
  NEWS_DATE_TO_KEY,
  NEWS_SEARCH_KEY,
  NEWS_SORT_KEY,
  SearchStorageKey,
  SortDirection,
  SortStorageKey,
  STORAGE_KEYS,
  TOOLS_CATEGORIES_KEY,
  TOOLS_DATE_FROM_KEY,
  TOOLS_DATE_TO_KEY,
  TOOLS_SEARCH_KEY,
  TOOLS_SORT_KEY,
} from '../interfaces/interfaces';

@Injectable({
  providedIn: 'root',
})
export class IndexedDBRepository {
  private dbName = 'stencasDB';
  private dbPromise?: Promise<IDBDatabase>;

  initDB(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 5);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const target = event.target as IDBOpenDBRequest;
        const db = target.result;

        for (const key of STORAGE_KEYS) {
          if (!db.objectStoreNames.contains(key)) {
            db.createObjectStore(key);
          }
        }
      };

      request.onsuccess = (event: Event) => {
        const target = event.target as IDBOpenDBRequest;
        resolve(target.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });

    return this.dbPromise;
  }

  async getCategoryFromKey(key: CategoryStorageKey): Promise<ICategory[]> {
    const db = await this.initDB();

    return await new Promise<ICategory[]>((resolve, reject) => {
      const transaction = db.transaction(key, 'readonly');
      const store = transaction.objectStore(key);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result as ICategory[]);
      request.onerror = () => reject(request.error);
    });
  }

  async addCategoryToKey(
    categories: ICategory[],
    key: CategoryStorageKey,
  ): Promise<void> {
    const db = await this.initDB();

    await this.clearDataFromKey(key);

    return await new Promise<void>(() => {
      const transaction = db.transaction(key, 'readwrite');
      const store = transaction.objectStore(key);

      for (const category of categories) {
        store.put(category, category.id);
      }
    });
  }

  async clearDataFromKey(key: CategoryStorageKey): Promise<void> {
    const db = await this.initDB();

    return await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(key, 'readwrite');
      const store = transaction.objectStore(key);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getSearchFromKey(key: SearchStorageKey): Promise<string> {
    const db = await this.initDB();

    return await new Promise<string>((resolve, reject) => {
      const transaction = db.transaction('search', 'readonly');
      const store = transaction.objectStore('search');

      const request = store.get(key);
      request.onsuccess = () => resolve(request.result as string);
      request.onerror = () => reject(request.error);
    });
  }

  async addSearchToKey(key: SearchStorageKey, search?: string): Promise<void> {
    const db = await this.initDB();

    return await new Promise<void>(() => {
      const transaction = db.transaction('search', 'readwrite');
      const store = transaction.objectStore('search');

      store.put(search, key);
    });
  }
  async getSortFromKey(
    key: SortStorageKey,
  ): Promise<{ key: string; direction: SortDirection }> {
    const db = await this.initDB();

    return await new Promise<{ key: string; direction: SortDirection }>(
      (resolve, reject) => {
        const transaction = db.transaction('sort', 'readonly');
        const store = transaction.objectStore('sort');

        const request = store.get(key);
        request.onsuccess = () =>
          resolve(request.result as { key: string; direction: SortDirection });
        request.onerror = () => reject(request.error);
      },
    );
  }

  async addSortToKey(
    key: SortStorageKey,
    sort: { key: string; direction: SortDirection },
  ): Promise<void> {
    const db = await this.initDB();

    return await new Promise<void>(() => {
      const transaction = db.transaction('sort', 'readwrite');
      const store = transaction.objectStore('sort');

      store.put(sort, key);
    });
  }

  async getDateFromKey(key: DateStorageKey): Promise<string | undefined> {
    const db = await this.initDB();

    return await new Promise<string | undefined>((resolve, reject) => {
      const transaction = db.transaction('date', 'readonly');
      const store = transaction.objectStore('date');

      const request = store.get(key);
      request.onsuccess = () => resolve(request.result as string | undefined);
      request.onerror = () => reject(request.error);
    });
  }

  addDateToKey(key: DateStorageKey, date?: string): Promise<void> {
    return this.initDB().then((db) => {
      return new Promise<void>((resolve, reject) => {
        const transaction = db.transaction('date', 'readwrite');
        const store = transaction.objectStore('date');

        const request = store.put(date, key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });
  }
}
